import { useMemo, useCallback } from 'react'

import {
  IconAdd,
  IconCheck,
  IconQuestion,
  IconReset,
  IconX,
} from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { useFormatTime } from '@webapp/core/components/FormattedTime/useFormatTime'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  ExpenseApprovalActions,
  type ExpenseApprovalActionEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseApprovalActionEnum'
import { type ExpenseApprovalLog } from '@webapp/modules/expense/entities/ExpenseApprovalLog'
import messages from './messages'
import { type ApprovalHistoryEvent } from '../../views/ApprovalHistory/ApprovalHistory'

interface ApprovalHistoryEventData {
  title: ApprovalHistoryEvent['title']
  icon: ApprovalHistoryEvent['icon']
  status: ApprovalHistoryEvent['status']
}

type EventDataMap = { [key: string]: ApprovalHistoryEventData }

export interface UseExpenseApprovalHistoryPresenterParams {
  logs: ExpenseApprovalLog[]
}

export const useExpenseApprovalHistoryPresenter = ({
  logs,
}: UseExpenseApprovalHistoryPresenterParams) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()
  const formatTime = useFormatTime()

  const eventDataByAction: EventDataMap = useMemo(
    () => ({
      [ExpenseApprovalActions.REQUEST]: {
        title: intl.formatMessage(messages.actionRequest),
        icon: <IconAdd />,
        status: null,
      },
      [ExpenseApprovalActions.FINAL_APPROVE]: {
        title: intl.formatMessage(messages.actionFinalApprove),
        icon: <IconCheck />,
        status: 'success' as const,
      },
      [ExpenseApprovalActions.DENY]: {
        title: intl.formatMessage(messages.actionDeny),
        icon: <IconX />,
        status: 'danger' as const,
      },
      [ExpenseApprovalActions.APPROVE_AND_PASS]: {
        title: intl.formatMessage(messages.actionApproveAndPass),
        icon: <IconCheck />,
        status: 'success' as const,
      },
      [ExpenseApprovalActions.RESUBMIT]: {
        title: intl.formatMessage(messages.actionResubmit),
        icon: <IconReset />,
        status: null,
      },
    }),
    [intl]
  )

  const formatDateTimeMetadata = useCallback(
    (dateTime: string) =>
      `${formatDate(new Date(dateTime))} ${formatTime(new Date(dateTime))}`,
    [formatDate, formatTime]
  )

  const getEventData = useCallback(
    (action: ExpenseApprovalActionEnum): ApprovalHistoryEventData =>
      eventDataByAction[action] ?? {
        title: intl.formatMessage(messages.actionUnknown),
        icon: <IconQuestion />,
        status: null,
      },
    [eventDataByAction, intl]
  )

  const approvalHistory: ApprovalHistoryEvent[] = useMemo(
    () =>
      logs.map((log) => {
        const eventData = getEventData(log.action)
        return {
          title: log.user.fullName,
          metadata: [
            eventData.title,
            log.dateTime ? formatDateTimeMetadata(log.dateTime) : '',
          ].filter((s): s is string => !!s),
          icon: eventData.icon,
          iconCircle: true,
          status: eventData.status,
        }
      }),
    [logs, getEventData, formatDateTimeMetadata]
  )

  return {
    data: {
      approvalHistory,
    },
  }
}
